import React, { useState, useContext, useMemo } from 'react';
import { UserContext, StudyPlanTask } from '../UserAndProfile/UserContext';
import { getCurrentWeek, getTaskRoute } from './studyPlanUtils';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, ToggleButtonGroup, ToggleButton, LinearProgress,
    Paper, Card, CardContent, Stack, Divider, useTheme, Chip,
    Tooltip, CardHeader
} from '@mui/material';
import { format, addMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isSameMonth, parseISO, isValid } from 'date-fns';

// --- Reusable Progress Display Component ---
interface ProgressDisplayProps {
    title: string;
    currentValue: number;
    targetValue: number;
    unit?: string; // e.g., 'tests', 'questions'
}

const ProgressDisplay: React.FC<ProgressDisplayProps> = ({ title, currentValue, targetValue, unit }) => {
    const theme = useTheme();
    const progress = targetValue > 0 ? Math.min(100, Math.round((currentValue / targetValue) * 100)) : 0;
    const displayValue = unit ? `${currentValue} / ${targetValue} ${unit}` : `${currentValue} / ${targetValue}`;

    return (
        <Box mb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="body1" fontWeight="medium">{title}</Typography>
                <Typography variant="body2" color="text.secondary">{displayValue}</Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 8, borderRadius: 4 }}
            />
             {/* Optional: Add percentage text */}
             {/* <Typography variant="caption" display="block" textAlign="right" mt={0.5}>{`${progress}%`}</Typography> */}
        </Box>
    );
};


// --- Calendar Helper Types ---
interface CalendarDay {
    date: Date;
    isCurrentMonth: boolean;
    tasks: StudyPlanTask[];
}

interface CalendarMonth {
    monthName: string;
    year: number;
    weeks: CalendarDay[][];
}


// --- Overall Tab Component ---

const OverallTab: React.FC = () => {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState<'overall' | 'thisWeek'>('overall');
    const theme = useTheme();

    const handleViewModeChange = (
        event: React.MouseEvent<HTMLElement>,
        newViewMode: 'overall' | 'thisWeek' | null,
    ) => {
        if (newViewMode !== null) {
            setViewMode(newViewMode);
        }
    };

    const studyPlan = user?.studyPlan;
    const today = useMemo(() => new Date(), []); // Memoize today's date

    // --- Calculate Progress Data ---
    const currentWeekData = useMemo(() => {
        if (!studyPlan) return null;
        return getCurrentWeek(studyPlan, today);
    }, [studyPlan, today]);

    const progressData = useMemo(() => {
        if (!user || !studyPlan) {
            return {
                diagnosticCompleted: 0,
                diagnosticTarget: 1,
                fullTestsCompleted: 0,
                fullTestsTarget: 0,
                reviewQuestionsCompleted: 0,
                reviewQuestionsTarget: 0,
                skillQuestionsCompleted: 0,
                skillQuestionsTarget: 0,
            };
        }

        const isOverall = viewMode === 'overall';

        // Diagnostic
        const diagnosticCompleted = user.testAttempts?.filter(t => t.testType === 'diagnostic' && t.completed).length ?? 0;

        const fullTestsCompletedOverall = user.testAttempts?.filter(t => t.testType === 'full' && t.completed).length ?? 0;
        const fullTestsTarget = studyPlan.overallPracticeTestGoal?.target ?? 0;
        const weeklyHasTestTask = !!currentWeekData?.tasks?.some(t => t.type === 'full-practice-test');
        const weeklyFullTestsCompleted = currentWeekData?.fullTestsCompleted ?? 0;

        // Review Questions
        const reviewGoalOverall = studyPlan.overallQuestionReviewGoal;
        const reviewGoalWeekly = currentWeekData?.weeklyReviewMissedQuestionGoal;
        const reviewQuestionsCompleted = isOverall
            ? (reviewGoalOverall?.current ?? 0)
            : (reviewGoalWeekly?.current ?? 0);
        const reviewQuestionsTarget = isOverall
            ? (reviewGoalOverall?.target ?? 0)
            : (reviewGoalWeekly?.target ?? 0);

        // Skill Practice Questions (Total)
        const skillGoalOverall = studyPlan.overallSkillPracticeQuestionGoal;
        const skillGoalWeekly = currentWeekData?.weeklySkillsPracticeQuestionGoal;
        const skillQuestionsCompleted = isOverall
            ? (skillGoalOverall?.current ?? 0)
            : (skillGoalWeekly?.current ?? 0);
        const skillQuestionsTarget = isOverall
            ? (skillGoalOverall?.target ?? 0)
            : (skillGoalWeekly?.target ?? 0);


        return {
            diagnosticCompleted,
            diagnosticTarget: 1,
            fullTestsCompleted: isOverall ? fullTestsCompletedOverall : weeklyFullTestsCompleted,
            fullTestsTarget: isOverall ? fullTestsTarget : (weeklyHasTestTask ? 1 : 0),
            reviewQuestionsCompleted,
            reviewQuestionsTarget,
            skillQuestionsCompleted,
            skillQuestionsTarget,
        };

    }, [user, studyPlan, viewMode, currentWeekData]);


    // --- Calendar Data Generation (Variation 1: Simple Grid) ---
    const calendarMonths = useMemo((): CalendarMonth[] => {
        if (!studyPlan?.weeklyPlans) return [];

        const allTasks: StudyPlanTask[] = studyPlan.weeklyPlans.flatMap(week => week.tasks || []);
        const monthsToShow: CalendarMonth[] = [];
        const firstMonthDate = startOfMonth(today);

        for (let i = 0; i < 2; i++) { // Generate data for 2 months
            const currentMonthDate = addMonths(firstMonthDate, i);
            const monthStart = startOfMonth(currentMonthDate);
            const monthEnd = endOfMonth(currentMonthDate);
            const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); // Start week on Monday
            const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

            const daysInMonthGrid = eachDayOfInterval({ start: startDate, end: endDate });
            const weeks: CalendarDay[][] = [];
            let currentWeek: CalendarDay[] = [];

            daysInMonthGrid.forEach((day, index) => {
                const dayTasks = allTasks.filter(task => {
                     // Check if task.dueDate is a valid date string before parsing
                    const dueDate = task.dueDate ? parseISO(task.dueDate) : null;
                    return dueDate && isValid(dueDate) && isSameDay(dueDate, day);
                });

                currentWeek.push({
                    date: day,
                    isCurrentMonth: isSameMonth(day, currentMonthDate),
                    tasks: dayTasks,
                });

                if ((index + 1) % 7 === 0) {
                    weeks.push(currentWeek);
                    currentWeek = [];
                }
            });

             monthsToShow.push({
                monthName: format(currentMonthDate, 'MMMM'),
                year: currentMonthDate.getFullYear(),
                weeks: weeks,
            });
        }

        return monthsToShow;

    }, [studyPlan, today]);


    if (!user || !studyPlan) {
        // Handle case where user or study plan is not loaded yet or doesn't exist
        return (
            <Box p={3}>
                <Typography>Loading study plan data or no plan exists...</Typography>
                {/* Optionally add a link to generate a plan if applicable */}
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, p: 2, mt: 2 }}>

            {/* Left Pane: Progress */}
            <Card sx={{ flex: 1, minWidth: 300 }}> {/* Adjust flex basis/minWidth as needed */}
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <CardHeader title="Study Plan Progress" sx={{p: 0}} />
                        {/*<Typography variant="h6">Progress</Typography>*/}
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={handleViewModeChange}
                            aria-label="View mode"
                            size="small"
                        >
                            <ToggleButton value="overall" aria-label="Overall">
                                Overall
                            </ToggleButton>
                            <ToggleButton value="thisWeek" aria-label="This Week">
                                This Week
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Stack spacing={2}>
                        {/* Only show Diagnostic progress in Overall view */}
                        {viewMode === 'overall' && (
                             <ProgressDisplay
                                title="Diagnostic Test"
                                currentValue={progressData.diagnosticCompleted}
                                targetValue={progressData.diagnosticTarget}
                            />
                        )}

                        {/* Full tests: overall count from testAttempts, weekly from snapshot */}
                        {(viewMode === 'overall' || progressData.fullTestsTarget > 0) && (
                            <ProgressDisplay
                                title="Full Length Tests"
                                currentValue={progressData.fullTestsCompleted}
                                targetValue={progressData.fullTestsTarget}
                                unit="tests"
                            />
                        )}

                        <ProgressDisplay
                            title="Review Missed Questions"
                            currentValue={progressData.reviewQuestionsCompleted}
                            targetValue={progressData.reviewQuestionsTarget}
                            unit="questions"
                        />
                        <ProgressDisplay
                            title="Skill Practice Questions"
                            currentValue={progressData.skillQuestionsCompleted}
                            targetValue={progressData.skillQuestionsTarget}
                            unit="questions"
                        />

                    </Stack>
                </CardContent>
            </Card>

            {/* Right Pane: Calendar (Variation 1) */}
            <Card sx={{ flex: 1.5 }}> {/* Adjust flex basis as needed */}
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <CardHeader title="Upcoming Tasks (Next 2 Months)" sx={{p: 0}} />
                    </Box >
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, overflowX: 'auto' }}>
                        {calendarMonths.map((monthData) => (
                            <Box key={`${monthData.monthName}-${monthData.year}`} sx={{ minWidth: 300, flexGrow: 1, flexBasis: 0 }}>
                                <Typography variant="h6" align="center" gutterBottom>
                                    {monthData.monthName} {monthData.year}
                                </Typography>
                                <Box display="flex" justifyContent="space-around" mb={1}>
                                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                        <Typography key={day} variant="caption" sx={{ flex: 1, textAlign: 'center', fontWeight: 'bold' }}>
                                            {day}
                                        </Typography>
                                    ))}
                                </Box>
                                {monthData.weeks.map((week, weekIndex) => (
                                    <Box key={weekIndex} display="flex" justifyContent="space-around">
                                        {week.map((day) => (
                                            <Paper
                                                key={day.date.toISOString()}
                                                variant="outlined"
                                                square
                                                sx={{
                                                    flex: 1,
                                                    height: 80, // Adjust height as needed
                                                    overflow: 'hidden',
                                                    p: 0.5,
                                                    position: 'relative',
                                                    backgroundColor: day.isCurrentMonth ? theme.palette.background.paper : theme.palette.action.hover,
                                                    opacity: day.isCurrentMonth ? 1 : 0.6,
                                                    borderTop: weekIndex === 0 ? 1 : 0, // Add top border to first week
                                                    borderLeft: day.date.getDay() === 1 ? 1 : 0, // Add left border to Monday
                                                    borderColor: 'divider'
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    component="div"
                                                    align="center"
                                                    sx={{
                                                        fontWeight: isSameDay(day.date, today) ? 'bold' : 'normal',
                                                        color: isSameDay(day.date, today) ? theme.palette.primary.main : 'inherit',
                                                        mb: 0.2
                                                    }}
                                                >
                                                    {format(day.date, 'd')}
                                                </Typography>
                                                <Stack spacing={0.2} sx={{ overflowY: 'auto', maxHeight: 'calc(100% - 20px)' }}>
                                                    {day.tasks.map(task => {
                                                        const route = getTaskRoute(task);
                                                        return (
                                                        <Tooltip title={task.title} key={task.id} placement="top">
                                                            <Chip
                                                                label={task.title.length > 15 ? task.title.substring(0, 12) + '...' : task.title}
                                                                size="small"
                                                                clickable={!!route && !task.isCompleted}
                                                                onClick={route && !task.isCompleted ? () => navigate(route) : undefined}
                                                                color={task.isCompleted ? 'success' : task.type === 'full-practice-test' ? 'secondary' : 'primary'}
                                                                variant={task.isCompleted ? 'filled' : 'outlined'}
                                                                sx={{
                                                                    height: 'auto',
                                                                    cursor: route && !task.isCompleted ? 'pointer' : 'default',
                                                                    '& .MuiChip-label': {
                                                                        display: 'block',
                                                                        whiteSpace: 'normal',
                                                                        fontSize: '0.65rem',
                                                                        lineHeight: 1.2
                                                                    },
                                                                }}
                                                            />
                                                        </Tooltip>
                                                        );
                                                    })}
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default OverallTab;